import { type FC, useMemo, useState } from 'react'

import { createGlobalStyle } from 'styled-components'
import { Pagination } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'

import { IconCloseLarge, IconPostStatsChat, IconReplayGraphRounded } from 'assets/svg/sprite'
import StatsPopup from 'components/UI/StatsPopup/StatsPopup'
import CaptionTextOverlay from 'features/PhotoEdit/CaptionTextOverlay/CaptionTextOverlay'
import ReplayGraphSVG from 'features/VideoPlayer/ReplayGraph'
import VideoPlayer from 'features/VideoPlayer/VideoPlayer'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { canonicalizeUrl } from 'helpers/signedUrl'
import { useUserProfile } from 'store'
import { type IStatsData } from 'types/interfaces/ITypes'

import style from '../../Messaging/Message/components/FullscreenMessage/_fullscreen_message.module.scss'

const FullscreenVaultModal: FC<{
  media: { type: string; [key: string]: any }[]
  closeFn: () => void
  startingIndex?: number
  post?: any
  message?: any
  scrollToMessage?: any
  selectedpostId?: any
  duration?: string
}> = ({
  media,
  closeFn,
  startingIndex = 0,
  post,
  message,
}) => {
  const userData = useUserProfile()

  const [showControls, setShowControls] = useState<boolean>(
    !window.location.pathname.includes('chat')
  )
  const [activeSlide, setActiveSlide] = useState<number>(startingIndex)
  const [overlayVisible, setOverlayVisible] = useState(true)

  const [showStatsPopup, setShowStatsPopup] = useState(false)

  const [showReplayGraph, setShowReplayGraph] = useState(false)
  const [mostViewedSegments, setMostViewedSegments] = useState<number[]>([])

  const toggleOverlay = () => {
    setOverlayVisible(prev => {
      const next = !prev
      if (!next) setShowReplayGraph(false)
      return next
    })
  }

  const messageStats = message?.message?.statistics

  const statsData: IStatsData | undefined = post
    ? {
        price: post.price,
        overallRevenue: post.overall_revenue,
        purchasesCount: post.purchases_count,
        purchasesSum: post.purchases_sum,
        tipsCount: post.tips_count,
        tipsSum: post.tips_sum,
        views: post.views_raw,
        uniqueViews: post.unique_views,
        uniqueImpressions: post.unique_impressions,
        conversionRate: post.conversion_rate,
      }
    : messageStats
      ? {
          price: message.message.price,
          overallRevenue: messageStats.purchases_sum,
          purchasesCount: messageStats.purchases_count,
          purchasesSum: messageStats.purchases_sum,
          tipsCount: 0,
          tipsSum: 0,
          views: messageStats.views,
          uniqueViews: messageStats.views_unique,
          uniqueImpressions: messageStats.impressions_unique,
          conversionRate: messageStats.conversion_rate,
        }
      : undefined


  const getResolvedType = (item: any): 'photo' | 'video' | null => {
    const isThumbPreview = !!item?.preview && item.preview.type === 'thumb'
    const isThumbFromVideo = isThumbPreview && typeof item?.duration === 'number'

    if (item?.type === 'photo') return 'photo'
    if (item?.type === 'poll') return 'photo'
    if (item?.type === 'goal') return 'photo'
    if (item?.type === 'video') return 'video'
    if (isThumbFromVideo) return 'video'
    if (isThumbPreview) return 'photo'

    return null
  }

  const activeMediaType = getResolvedType(media?.[activeSlide])
  const isActivePhoto = activeMediaType === 'photo'

    const getCachedOrRawSrc = (url?: string | null): string => {
    if (!url) return ''

    const cacheKey = canonicalizeUrl(url)
    const cached = cacheKey ? getCachedBlob(cacheKey) : null

    return cached ?? url
    }

    const getForegroundSrc = (item: any): string => {
    return getCachedOrRawSrc(
        item?.src ||
        item?.url ||
        ''
    )
    }

  const getBackgroundSrc = (item: any, fallback?: string): string => {
    return getCachedOrRawSrc(
        item?.thumb_url ||
        item?.active_thumb ||
        item?.preview?.src ||
        fallback ||
        item?.url ||
        item?.src ||
        ''
    )
  }
  const getVideoDurationForItem = (item: any): number | undefined => {
    if (typeof item?.duration === 'number' && !Number.isNaN(item.duration)) {
      return item.duration
    }

    const id = item?.id
    if (!id) return undefined

    const source = message ?? post

    const fromVideos = source?.videos?.find((v: any) => v.id === id)
    if (typeof fromVideos?.duration === 'number' && !Number.isNaN(fromVideos.duration)) {
      return fromVideos.duration
    }

    const fromVideosPreview = source?.videos_preview?.find((v: any) => v.id === id)
    if (typeof fromVideosPreview?.duration === 'number' && !Number.isNaN(fromVideosPreview.duration)) {
      return fromVideosPreview.duration
    }

    return undefined
  }

  const renderMedia = () => {
    if (!media?.length) return null

    return media.map((item: any, key: number) => {
      const resolvedType = getResolvedType(item)
      const foregroundSrc = getForegroundSrc(item)
      const backgroundSrc = getBackgroundSrc(item, foregroundSrc)

      if (!resolvedType || !foregroundSrc) return null

      if (resolvedType === 'photo') {
        return (
          <SwiperSlide key={key}>
            <div
              className={`${style.fullscreen_photo_container} ${
                item.orientation === 'Landscape'
                  ? style.fullscreen_photo_container_landscape
                  : ''
              }`}
              onClick={toggleOverlay}
            >
              <div className={style.container_overlay}></div>

                <img
                    src={backgroundSrc}
                    alt="vault media background"
                    className={style.fullscreen_photo_background}
                />

                <img
                    src={foregroundSrc}
                    alt="vault media"
                    className={style.fullscreen_photo}
                />

              <CaptionTextOverlay
                text={item.text?.value}
                xPercent={item.text?.x}
                yPercent={item.text?.y}
                zIndex={5}
              />
            </div>
          </SwiperSlide>
        )
      }

      if (resolvedType === 'video') {
        const videoUrl = item.url || item.src || ''
        const poster = getBackgroundSrc(item, foregroundSrc) || undefined
        const duration = getVideoDurationForItem(item)

        const videoOwnerId =
          item.user_id ??
          message?.user_id ??
          message?.user?.id ??
          post?.user_id ??
          post?.user?.id

        const rawSegments = Array.isArray(item.aggregated_segment_data)
          ? item.aggregated_segment_data
          : []

        const hasReplayData = rawSegments.length > 0
        const isOwner = userData?.id === videoOwnerId

        const showMostReplayedButton =
          overlayVisible &&
          isOwner &&
          hasReplayData

        return (
          <SwiperSlide key={key}>
            <div
              className={style.fullscreen_video_container}
              onClick={toggleOverlay}
            >
              {poster && (
                <img
                  src={poster}
                  alt="vault video background"
                  className={style.fullscreen_video_background}
                />
              )}

              <div className={style.container_overlay}></div>

              <VideoPlayer
                  segment_data={rawSegments}
                  poster={poster}
                  encoded={item.encoded}
                  url={videoUrl}
                  minimized={false}
                  videoId={item.id}
                  videoUserId={videoOwnerId}
                  duration={duration}
                  isActive={activeSlide === key}
                  controlsDisabled={false}
                  showControls={overlayVisible}
                  pauseWhenPreviewClosed={false}
                  ownerType={message ? 'message' : 'post'}
                  setShowControls={() => {
                      setShowControls(!showControls)
                  }}
              />

              {showMostReplayedButton && (
                <>
                  <button
                    className={style.replay_graph_toggle}
                    onClick={e => {
                      e.stopPropagation()
                      setMostViewedSegments(rawSegments)
                      setShowReplayGraph(prev => !prev)
                    }}
                  >
                    <IconReplayGraphRounded color="#fff" />
                    <span>Most Replayed</span>
                  </button>

                  {showReplayGraph && mostViewedSegments.length > 0 && (
                    <div
                      className={style.replay_graph_bar}
                      onClick={e => e.stopPropagation()}
                    >
                      <ReplayGraphSVG
                        data={mostViewedSegments}
                        // width={window.innerWidth}
                        height={75}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </SwiperSlide>
        )
      }

      return null
    })
  }

  return (
    <div className={style.container}>
      <div className={style.close_icon} onClick={closeFn}>
        <IconCloseLarge color="#ffffff" />
      </div>

      <PaginationDots />

      <Swiper
        initialSlide={startingIndex}
        onSlideChange={(swiper: SwiperClass) => {
            setActiveSlide(swiper.activeIndex)
            setShowReplayGraph(false)
        }}
        pagination={{ clickable: true, dynamicBullets: true }}
        modules={[Pagination]}
        className={`${style.fullscreen_swiper} custom-swiper ${
          !overlayVisible ? 'custom-swiper--overlayHidden' : ''
        }`}
      >
        {renderMedia()}
      </Swiper>
      {overlayVisible && statsData && (
<button
  type="button"
  className={`${style.fullscreen_stats_button} ${
    isActivePhoto ? style.fullscreen_stats_button_photo : ''
  }`}
  onClick={e => {
    e.preventDefault()
    e.stopPropagation()
    setShowReplayGraph(false)
    setShowStatsPopup(true)
  }}
>
  <IconPostStatsChat />
  <span>View Stats</span>
</button>
      )}

      {showStatsPopup && statsData && (
  <div
    className={style.fullscreen_stats_backdrop}
    onClick={e => {
      e.stopPropagation()

      if (e.target === e.currentTarget) {
        setShowStatsPopup(false)
      }
    }}
  >
    <div
      className={style.fullscreen_stats_card}
      onClick={e => e.stopPropagation()}
    >
      <StatsPopup
        centered
        statsData={statsData}
        closeStats={() => setShowStatsPopup(false)}
      />
    </div>
  </div>
)}
    </div>
  )
}

const PaginationDots = createGlobalStyle`
  .custom-swiper .swiper-pagination {
    position: absolute !important;
    top: 78%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .custom-swiper .swiper-pagination-bullet {
    background:rgb(243, 246, 249) !important;
    width: 10px !important;
    height: 10px !important;
    opacity: 0.9 !important;
  }

  .custom-swiper .swiper-pagination-bullet-active {
    background: #286395 !important;
    opacity: 1 !important;
  }

  .custom-swiper.custom-swiper--overlayHidden .swiper-pagination {
    opacity: 0;
    pointer-events: none;
  }
`

export default FullscreenVaultModal